import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  OcBreadcrumbComponent,
  OcBreadcrumbItem,
  OcDrawerComponent,
  OcDrawerHeaderComponent,
  OcMessageComponent,
  OcModalComponent,
  OcModalFooterComponent,
  OcToastService,
  OcTooltipDirective,
} from 'otimus-library'
import { Subject, Subscription, takeUntil } from 'rxjs'
import { ScheduleDomainService } from '../../schedule-domain.service'
import { AppointmentDetailsComponent } from '../appointment-details/appointment-details.component'
import { AgendaSocketService } from '../agenda-calendar/agenda-socket.service'
import { ScheduleSocketService } from '../agenda-calendar/schedule-socket.service'
import { AuthService } from 'src/app/services/auth.service'
import { InternationalizationService } from 'src/app/services/internationalization.service'
import { GenexusService } from 'src/app/modules/billing/services/genexus.service'
import { LockedTimeSlotService } from '../../../../services/locked-time-slot.service'
import { LockedTimeSlotType } from '../../../appointment-calendar/interface/time-slot'
import {
  AgendaDateRange,
  AgendaEventClick,
  AgendaEventMove,
  AgendaSchedule,
  AppointmentCancelledEvent,
  AppointmentCreatedEvent,
  AppointmentSlot,
  AppointmentTransferredEvent,
  BlockedSlot,
  BlockedSlotsUpdatedEvent,
  CalendarContext,
  CalendarSlot,
  FreeSlot,
  ScheduleGridResponse,
  ScheduleTimeGrid,
  getStatusColor,
} from '../agenda-calendar/agenda-calendar.models'

interface DenseColumn {
  scheduleId: number
  scheduleName: string
  professional?: string
  color?: string
  date: string
  key: string
  scheduled: number
  free: number
}

interface DenseDateGroup {
  date: string
  weekdayLabel: string
  dayLabel: string
  span: number
}

interface DenseCellSlot {
  type: 'free' | 'appointment' | 'blocked' | 'continuation' | 'group'
  // For 'group' the `slot` is the EARLIEST appointment of the group (representative card).
  // For other types it is the underlying CalendarSlot.
  slot: CalendarSlot
  startTime: string
  endTime: string
  observation?: string
  services?: { id: number; name: string }[]
  // Only for type 'continuation': info about the appointment that started in an earlier row.
  ownerName?: string
  ownerStartTime?: string
  // Only for type 'group': all appointments belonging to the group, sorted by start ASC.
  groupSlots?: AppointmentSlot[]
  // Only for type 'group': the stable group key (used to track user-driven ungrouping).
  groupKey?: string
  // Only for type 'appointment' (individual cards that COULD be grouped with neighbors
  // but are currently kept separate by user choice): hint flag to render the chain icon.
  canGroupWithNeighbors?: boolean
}

interface DenseRow {
  time: string
  cellsByKey: Record<string, DenseCellSlot[]>
}

/**
 * Dense schedule grid view.
 *
 * Renders the same schedule data as the FullCalendar-based view, but using a plain
 * CSS grid where row height grows with the tallest card in the row. Cards never
 * truncate patient name or procedure — they wrap and the row expands.
 */
@Component({
  selector: 'app-dense-schedule-grid',
  standalone: true,
  imports: [
    CommonModule,
    OcBreadcrumbComponent,
    OcTooltipDirective,
    OcDrawerComponent,
    OcDrawerHeaderComponent,
    OcMessageComponent,
    OcModalComponent,
    OcModalFooterComponent,
    AppointmentDetailsComponent,
  ],
  // Socket services are component-scoped (same approach used by agenda-calendar) so each
  // dense view manages its own connection lifecycle.
  providers: [ScheduleDomainService, AgendaSocketService, ScheduleSocketService],
  templateUrl: './dense-schedule-grid.component.html',
  styleUrl: './dense-schedule-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DenseScheduleGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dateRange: AgendaDateRange = (() => {
    const today = new Date().toISOString().split('T')[0]
    return { start: today, end: today }
  })()
  @Input() schedules: AgendaSchedule[] = []
  @Input() locale: 'pt-br' | 'en' | 'es' = 'pt-br'
  @Input() draggableStatusIds: number[] = [1, 2]
  @Input() allowTransfer = true
  @Input() allowFitIn = false
  @Input() allowScheduling = true
  // DENSE_VIEW_MOCK: when provided, skips HTTP and uses these grids directly (preview/dev only).
  @Input() mockGrids?: ScheduleGridResponse[]
  // Slot data is owned by the parent so both views share the same source of truth and toggling
  // between them is instant. Falls back to `mockGrids` for the standalone preview.
  @Input() gridResponse: ScheduleGridResponse[] = []
  @Input() loading = false

  @Output() eventClick = new EventEmitter<AgendaEventClick>()
  @Output() eventMove = new EventEmitter<AgendaEventMove>()
  // Emitted when the user moves 2+ appointments at once from a group drag.
  // The parent should use the batch transfer endpoint to apply them atomically.
  @Output() eventMoveBatch = new EventEmitter<AgendaEventMove[]>()
  @Output() historyClick = new EventEmitter<AppointmentSlot>()
  // Emitted when the user clicks "Abrir Agendamento" inside the details drawer.
  // The parent navigates to the appointment detail URL (via Genexus or equivalent).
  @Output() openAppointmentClick = new EventEmitter<AppointmentSlot>()
  // Emitted when a socket event indicates the parent should re-fetch slot content.
  @Output() reloadRequest = new EventEmitter<void>()

  protected columns: DenseColumn[] = []
  protected dateGroups: DenseDateGroup[] = []
  protected isMultiDay = false
  protected rows: DenseRow[] = []
  protected scheduledCount = 0
  protected freeCount = 0

  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>

  // Auto-scroll while dragging near the top/bottom edges of the grid scroller.
  private autoScrollRafId: number | null = null
  private autoScrollPointerY: number | null = null
  private static readonly AUTO_SCROLL_EDGE_PX = 60
  private static readonly AUTO_SCROLL_MAX_SPEED_PX = 18

  protected dragOverKey: string | null = null
  protected dragOverTime: string | null = null
  protected draggingApptId: number | string | null = null
  // Floating drag preview (custom ghost) — replaces the browser's default drag image,
  // which scaled to the source card width and showed the original time. The preview is
  // appended to <body> and follows the cursor; its time pill updates from dragOverTime.
  private dragPreviewEl: HTMLElement | null = null
  private dragPreviewTimeEl: HTMLSpanElement | null = null
  private dragPreviewLastTime: string | null = null
  private dragPreviewOffsetX = 0
  private dragPreviewOffsetY = 0
  private static readonly TRANSPARENT_DRAG_IMAGE: HTMLImageElement = (() => {
    const img = new Image()
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
    return img
  })()
  // When the user is dragging a group card, holds the full list of appointments in that group.
  // null when dragging an individual card (or not dragging).
  protected draggingGroup: AppointmentSlot[] | null = null
  // Where the drop indicator line should appear when dragging over a populated cell.
  //   anchorIndex = index of the card the cursor is closest to
  //   position = 'above' | 'below' that card
  // null when dragging over an empty cell (no anchor needed, falls back to row.time).
  protected dropPreview: {
    columnKey: string
    rowTime: string
    anchorIndex: number
    position: 'above' | 'below'
  } | null = null

  // Pending move that needs user confirmation (collision / fit-in scenarios).
  // `isCleanMove` is set when the drop lands on a pre-configured free slot and the domain
  // returned `moved` — i.e. no fit-in and no "outside the template" surprise, so the modal
  // shows only the source/target summary without warnings.
  protected pendingMove: {
    moveData: AgendaEventMove
    isFitIn: boolean
    isCleanMove?: boolean
    patientName: string
    sourceTime: string
    sourceScheduleName: string
    targetTime: string
    targetScheduleName: string
    coveredMinutes: number
    totalMinutes: number
  } | null = null
  protected pendingMoveTimeError: string | null = null

  // Group keys that the user has explicitly ungrouped during this session.
  // Resets on refresh — auto-grouping is the default.
  protected ungroupedKeys = new Set<string>()
  // IDs of individual appointments the user pulled OUT of a group (won't auto-rejoin).
  protected detachedAppointmentIds = new Set<number | string>()
  // Currently open group drawer (null when closed).
  protected openGroupDrawer: {
    groupKey: string
    appointments: AppointmentSlot[]
    column: DenseColumn
  } | null = null
  // Currently open appointment details drawer (null when closed). When the user opens it
  // from the group drawer's list, `fromGroupKey` is populated so the "back" button can
  // restore the group view.
  protected openDetailsDrawer: {
    appointment: AppointmentSlot
    fromGroupKey: string | null
  } | null = null
  // Snapshot of the group drawer state stashed while the user navigates into a single
  // appointment from the list. Restored when they hit "Voltar".
  private stashedGroupDrawer: {
    groupKey: string
    appointments: AppointmentSlot[]
    column: DenseColumn
  } | null = null
  // Pending group move (drop on a group card): user must choose move-all or move-some.
  protected pendingGroupMove: {
    appointments: AppointmentSlot[]
    targetColumn: DenseColumn
    targetTime: string
    selectedIds: Set<number | string>
    mode: 'choose' | 'pick-some'
  } | null = null

  // Pending cascade shift: when a drop between cards forces neighbors to move +1min each,
  // we ask the user to confirm the multi-move.
  protected pendingShiftMove: {
    movingAppointment: AppointmentSlot
    newStart: string
    newEnd: string
    targetColumn: DenseColumn
    shiftMoves: AgendaEventMove[]
  } | null = null

  private calendarSlots: CalendarSlot[] = []
  private scheduleGrids: ScheduleTimeGrid[] = []
  private blockedSlots: BlockedSlot[] = []
  private readonly destroy$ = new Subject<void>()
  private socketSubscription: Subscription | null = null
  // Schedules currently joined on the socket — kept so we can leave when they change.
  private joinedScheduleIds: number[] = []

  // Minimum granularity (in minutes) for the dense grid rows. Slot start times
  // are rounded DOWN to this multiple to decide which row they belong to —
  // e.g. 07:00 and 07:01 both fall into the "07:00" bucket when BUCKET_MIN = 5.
  private static readonly BUCKET_MIN = 5

  constructor(
    private readonly domain: ScheduleDomainService,
    private readonly cdr: ChangeDetectorRef,
    private readonly scheduleSocket: ScheduleSocketService,
    private readonly authService: AuthService,
    private readonly toastService: OcToastService,
    private readonly lockedTimeSlotService: LockedTimeSlotService,
    private readonly genexusService: GenexusService,
    private readonly translateService: InternationalizationService
  ) {}

  private lastLockedClick: { eventKey: string; time: number } | null = null
  // After a successful force-entry on a slot we silence further "locked" toasts for
  // the same slot for a short window — otherwise a follow-up click (e.g. the user
  // completing a literal double-click after the warning toast told them to "click
  // twice") would be treated as a fresh first click and re-show the warning.
  private recentForceEntry: { eventKey: string; time: number } | null = null
  private static readonly FORCE_ENTRY_COOLDOWN_MS = 1500

  // Slots locked when the current HTML5 drag started — mirrors agenda-calendar's draggingSlotInfo,
  // but tracks a list so group drags can release every appointment they locked.
  private draggingLockedSlots: { start: string; scheduleId: number }[] = []
  private unlockDebounceTimer: ReturnType<typeof setTimeout> | null = null

  ngOnInit(): void {
    this.syncDomainPermissions()
    this.subscribeToSocketEvents()
    // Standalone preview path: when mockGrids is provided and there's no parent feeding gridResponse,
    // render directly from the mock data.
    if (this.mockGrids && this.schedules.length > 0) {
      this.applyGrids(this.mockGrids)
      this.cdr.markForCheck()
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allowFitIn'] || changes['allowTransfer']) {
      this.syncDomainPermissions()
    }
    if (changes['schedules']) {
      const previous = changes['schedules'].previousValue as AgendaSchedule[] | undefined
      if (previous?.length) {
        this.scheduleSocket.leaveSchedule(previous.map((s) => s.id))
      }
      if (this.schedules.length > 0) {
        const scheduleIds = this.schedules.map((s) => s.id)
        this.scheduleSocket.joinSchedule(scheduleIds)
        this.joinedScheduleIds = [...scheduleIds]
      }
    }
    if (
      changes['schedules'] ||
      changes['dateRange'] ||
      changes['gridResponse'] ||
      changes['mockGrids']
    ) {
      if (this.schedules.length === 0) {
        this.columns = []
        this.rows = []
        this.scheduledCount = 0
        this.freeCount = 0
        this.cdr.markForCheck()
        return
      }
      // Mock takes precedence for the standalone preview; otherwise render the parent-provided data.
      const grids = this.mockGrids ?? this.gridResponse
      this.applyGrids(grids)
      this.cdr.markForCheck()
    }
  }

  ngOnDestroy(): void {
    if (this.joinedScheduleIds.length) {
      this.scheduleSocket.leaveSchedule(this.joinedScheduleIds)
    }
    this.socketSubscription?.unsubscribe()
    this.socketSubscription = null
    this.stopAutoScroll()
    this.removeDragPreview()
    this.cancelUnlockDebounce()
    this.destroy$.next()
    this.destroy$.complete()
  }

  private get calendarContext(): CalendarContext {
    return { slots: this.calendarSlots, grids: this.scheduleGrids, blockedSlots: this.blockedSlots }
  }

  // The dense view and the agenda-calendar share the same ScheduleDomainService instance per
  // component (provided at component level), so each view pushes its own permission flags
  // before invoking moveSlot. This keeps fit-in / transfer enforcement centralized.
  private syncDomainPermissions(): void {
    this.domain.allowFitIn = this.allowFitIn
    this.domain.allowTransfer = this.allowTransfer
  }

  private applyGrids(grids: ScheduleGridResponse[]): void {
    const allowedDates = this.dateRange.specificDates?.length
      ? this.dateRange.specificDates
      : this.domain.listDatesInRange(this.dateRange.start, this.dateRange.end)
    const { scheduleGrids, calendarSlots, blockedSlots } = this.domain.mapGridResponse(
      grids,
      this.schedules,
      allowedDates
    )
    this.scheduleGrids = scheduleGrids
    this.blockedSlots = blockedSlots
    this.calendarSlots = calendarSlots
    this.buildView()
  }

  private buildView(): void {
    const dates = this.domain.getDateList(this.dateRange)
    const multiDay = dates.length > 1
    this.isMultiDay = multiDay

    this.columns = []
    this.dateGroups = []
    for (const date of dates) {
      this.dateGroups.push({
        date,
        weekdayLabel: this.domain.formatWeekday(date, this.locale),
        dayLabel: this.domain.formatDate(date),
        span: this.schedules.length,
      })
      for (const schedule of this.schedules) {
        this.columns.push({
          scheduleId: schedule.id,
          scheduleName: schedule.name,
          professional: schedule.professional,
          color: schedule.color,
          date,
          key: multiDay ? `${schedule.id}-${date}` : String(schedule.id),
          scheduled: 0,
          free: 0,
        })
      }
    }
    const columnByKey = new Map(this.columns.map((c) => [c.key, c]))

    // Bucket helper: rounds an "HH:MM" time DOWN to the nearest 5-minute multiple.
    //   "07:00" -> "07:00"
    //   "07:01" -> "07:00"
    //   "07:04" -> "07:00"
    //   "07:05" -> "07:05"
    //   "07:11" -> "07:10"
    const toBucket = (time: string): string => {
      const [hh, mm] = time.split(':').map(Number)
      const bucketMinute =
        Math.floor(mm / DenseScheduleGridComponent.BUCKET_MIN) *
        DenseScheduleGridComponent.BUCKET_MIN
      return `${String(hh).padStart(2, '0')}:${String(bucketMinute).padStart(2, '0')}`
    }

    // Build the list of template buckets per column key. Each template range gets its
    // start time bucketed; sub-bucket variations collapse together (e.g. 07:00 and 07:01
    // both belong to bucket 07:00).
    const templateBucketsByCol = new Map<string, Set<string>>()
    for (const grid of this.scheduleGrids) {
      for (const slot of grid.slots) {
        const date = slot.start.split('T')[0]
        const colKey = multiDay ? `${grid.scheduleId}-${date}` : String(grid.scheduleId)
        if (!templateBucketsByCol.has(colKey)) templateBucketsByCol.set(colKey, new Set())
        templateBucketsByCol.get(colKey)!.add(toBucket(this.toTime(slot.start)))
      }
    }

    // Collect every row time as a 5-min bucket. View is agnostic about templates —
    // it surfaces every bucket where ANY slot or template starts.
    const timesSet = new Set<string>()
    for (const buckets of templateBucketsByCol.values()) {
      for (const b of buckets) timesSet.add(b)
    }
    for (const slot of this.calendarSlots) {
      timesSet.add(toBucket(this.toTime(slot.start)))
    }

    const colKeyForSlot = (slot: CalendarSlot): string =>
      multiDay ? `${slot.scheduleId}-${slot.start.split('T')[0]}` : String(slot.scheduleId)

    const times = [...timesSet].sort()

    const rows: DenseRow[] = times.map((time) => ({
      time,
      cellsByKey: Object.fromEntries(this.columns.map((c) => [c.key, [] as DenseCellSlot[]])),
    }))

    let scheduled = 0
    let free = 0

    for (const slot of this.calendarSlots) {
      const colKey = colKeyForSlot(slot)
      const rowTime = toBucket(this.toTime(slot.start))
      const row = rows.find((r) => r.time === rowTime)
      if (!row) continue
      if (!(colKey in row.cellsByKey)) continue

      row.cellsByKey[colKey].push({
        type: slot.type,
        slot,
        startTime: this.domain.formatTime(slot.start),
        endTime: this.domain.formatTime(slot.end),
        observation: this.extractObservation(slot),
        services: this.extractServices(slot),
      })

      const col = columnByKey.get(colKey)
      if (slot.type === 'appointment') {
        scheduled++
        if (col) col.scheduled++
      } else if (slot.type === 'free') {
        free++
        if (col) col.free++
      }
    }

    // When an appointment and a free slot fall in the same 5-min bucket of the same column,
    // the free slot is effectively occupied — drop it so we don't render a redundant "Livre"
    // card next to the appointment. Also decrement the free counter to keep the column header
    // count consistent with what's actually rendered.
    for (const row of rows) {
      for (const colKey of Object.keys(row.cellsByKey)) {
        const cells = row.cellsByKey[colKey]
        if (cells.length < 2) continue
        const hasOccupant = cells.some((c) => c.type === 'appointment' || c.type === 'blocked')
        if (!hasOccupant) continue
        const before = cells.length
        row.cellsByKey[colKey] = cells.filter((c) => c.type !== 'free')
        const removed = before - row.cellsByKey[colKey].length
        if (removed > 0) {
          free -= removed
          const col = columnByKey.get(colKey)
          if (col) col.free -= removed
        }
      }
    }

    // For appointments whose range crosses multiple buckets of THIS column's own template,
    // mark those bucket rows as "continuation" — meaning the cell is unavailable because the
    // previous appointment is still ongoing. Rows that exist only because OTHER columns have
    // a template there are NOT marked: they are foreign to this column and remain empty.
    for (const slot of this.calendarSlots) {
      if (slot.type !== 'appointment') continue
      const colKey = colKeyForSlot(slot)
      const ownerBucket = toBucket(this.toTime(slot.start))
      const endMs = this.domain.toMs(slot.end)
      const ownerName = (slot as AppointmentSlot).patient?.name ?? ''
      const ownerStartTime = this.domain.formatTime(slot.start)
      const ownTemplateBuckets = templateBucketsByCol.get(colKey) ?? new Set<string>()

      for (const row of rows) {
        if (row.time <= ownerBucket) continue
        const rowMs = this.domain.toMs(`${slot.start.split('T')[0]}T${row.time}:00`)
        if (rowMs >= endMs) break
        if (!(colKey in row.cellsByKey)) continue
        // Skip foreign buckets (no template of this column starts inside this 5-min slot).
        if (!ownTemplateBuckets.has(row.time)) continue
        // Inject continuation only if the cell is empty (no real slot fights with the marker).
        if (row.cellsByKey[colKey].length === 0) {
          row.cellsByKey[colKey].push({
            type: 'continuation',
            slot,
            startTime: row.time,
            endTime: this.domain.formatTime(slot.end),
            ownerName,
            ownerStartTime,
          })
        }
      }
    }

    for (const row of rows) {
      for (const key of Object.keys(row.cellsByKey)) {
        // Chronological order: earlier startTime first. Inside the same minute,
        // appointment id ascending breaks ties deterministically.
        row.cellsByKey[key].sort((a, b) => {
          const cmp = a.startTime.localeCompare(b.startTime)
          if (cmp !== 0) return cmp
          const aId = a.type === 'appointment' ? Number((a.slot as AppointmentSlot).id) : 0
          const bId = b.type === 'appointment' ? Number((b.slot as AppointmentSlot).id) : 0
          return aId - bId
        })
      }
    }

    // Auto-group appointments inside each cell when consecutive cards share patient + status
    // and start no more than 1 minute apart. Groups represent multiple sequential exams for the
    // same person and collapse into a single visual card. The user can opt out via ungroupedKeys.
    for (const row of rows) {
      for (const key of Object.keys(row.cellsByKey)) {
        row.cellsByKey[key] = this.groupAppointmentsInCell(row.cellsByKey[key], key, row.time)
      }
    }

    this.rows = rows
    this.scheduledCount = scheduled
    this.freeCount = free
  }

  // Walks the chronologically-sorted cell items and merges consecutive appointment cards
  // that share patient.id + status.id and have a gap of <=1min between starts into a single
  // 'group' DenseCellSlot. Other types (free/blocked/continuation) act as boundaries.
  private groupAppointmentsInCell(
    cells: DenseCellSlot[],
    colKey: string,
    rowTime: string
  ): DenseCellSlot[] {
    if (cells.length < 2) {
      // Still need to compute canGroupWithNeighbors hint when cells.length === 1? It's always
      // false for a single card, so just return early.
      return cells
    }
    const result: DenseCellSlot[] = []
    let i = 0
    while (i < cells.length) {
      const current = cells[i]
      if (current.type !== 'appointment') {
        result.push(current)
        i++
        continue
      }
      // Try to extend a group from `current`.
      const groupSlots: AppointmentSlot[] = [current.slot as AppointmentSlot]
      let j = i + 1
      while (j < cells.length) {
        const next = cells[j]
        if (next.type !== 'appointment') break
        const a = groupSlots[groupSlots.length - 1]
        const b = next.slot as AppointmentSlot
        if (!this.canGroupAppointments(a, b)) break
        groupSlots.push(b)
        j++
      }

      if (groupSlots.length >= 2) {
        // Filter out detached individuals — they should not auto-rejoin once removed by user.
        const eligible = groupSlots.filter((s) => !this.detachedAppointmentIds.has(s.id))
        const earliest = eligible[0]
        const groupKey = earliest ? this.makeGroupKey(colKey, rowTime, earliest) : ''

        // Group must still satisfy the grouping rules after detaching.
        if (eligible.length >= 2 && groupKey && !this.ungroupedKeys.has(groupKey)) {
          result.push({
            type: 'group',
            slot: earliest, // representative card uses earliest appointment data
            startTime: this.domain.formatTime(earliest.start),
            endTime: this.domain.formatTime(eligible[eligible.length - 1].end),
            groupSlots: eligible,
            groupKey,
          })
          // Detached ones (if any) and ineligible-for-group ones stay as individual cards.
          const detachedFromThisRun = groupSlots.filter((s) =>
            this.detachedAppointmentIds.has(s.id)
          )
          for (const d of detachedFromThisRun) {
            result.push(this.makeAppointmentCellWithGroupHint(d, true))
          }
          i = j
          continue
        }

        // User asked to ungroup — render as individuals, but mark them as groupable so the
        // chain icon shows up.
        for (const slot of groupSlots) {
          result.push(this.makeAppointmentCellWithGroupHint(slot, true))
        }
        i = j
        continue
      }

      // Just one appointment in the run — could it group with NEXT (if NEXT itself had failed
      // to extend a previous run)? No: the check above already considered (a,b). So single.
      result.push(current)
      i++
    }
    return result
  }

  private canGroupAppointments(a: AppointmentSlot, b: AppointmentSlot): boolean {
    if (!a.patient || !b.patient) return false
    if (a.patient.id !== b.patient.id) return false
    if (!a.status || !b.status) return false
    if (a.status.id !== b.status.id) return false
    const diffMs = this.domain.toMs(b.start) - this.domain.toMs(a.start)
    return diffMs >= 0 && diffMs <= 60_000
  }

  private makeGroupKey(colKey: string, rowTime: string, earliest: AppointmentSlot): string {
    const patientId = earliest.patient?.id ?? '?'
    const statusId = earliest.status?.id ?? '?'
    return `${colKey}|${rowTime}|${patientId}|${statusId}`
  }

  private makeAppointmentCellWithGroupHint(
    slot: AppointmentSlot,
    canGroup: boolean
  ): DenseCellSlot {
    return {
      type: 'appointment',
      slot,
      startTime: this.domain.formatTime(slot.start),
      endTime: this.domain.formatTime(slot.end),
      canGroupWithNeighbors: canGroup,
    }
  }

  // --- Socket event subscriptions ---
  //
  // Mirrors the agenda-calendar's behavior so the dense view stays in sync when other users
  // create, transfer, cancel or block appointments, and when slots are locked/unlocked.
  // For created/cancelled/blocks we just trigger a debounced refetch. For transferred we
  // try to apply the move locally to avoid a full reload.
  private subscribeToSocketEvents(): void {
    this.socketSubscription = new Subscription()

    this.socketSubscription.add(
      this.scheduleSocket.appointmentCreated$.subscribe((event: AppointmentCreatedEvent) => {
        if (!this.schedules.some((s) => s.id === event.scheduleId)) return
        this.reloadRequest.emit()
      })
    )

    this.socketSubscription.add(
      this.scheduleSocket.appointmentTransferred$.subscribe(
        (event: AppointmentTransferredEvent) => {
          const isCurrent = event.scheduleIds.some((id: number) =>
            this.schedules.some((s) => s.id === id)
          )
          if (!isCurrent) return
          this.applyTransferredEvent(event)
        }
      )
    )

    this.socketSubscription.add(
      this.scheduleSocket.appointmentCancelled$.subscribe((event: AppointmentCancelledEvent) => {
        if (!this.schedules.some((s) => s.id === event.scheduleId)) return
        this.reloadRequest.emit()
      })
    )

    this.socketSubscription.add(
      this.scheduleSocket.blockedSlotsUpdated$.subscribe((data: BlockedSlotsUpdatedEvent[]) => {
        const isCurrent = data.some((d) => this.schedules.some((s) => s.id === d.scheduleId))
        if (!isCurrent) return
        this.reloadRequest.emit()
      })
    )

    this.socketSubscription.add(
      this.scheduleSocket.lockedTimeSlotsUpdated$.subscribe((data: LockedTimeSlotType[]) => {
        const currentUserId = this.authService.decryptedData?.user_id
        let mutated = false
        for (const lock of data) {
          if (lock.userId === currentUserId) continue
          const slot = this.findSlotByDateTime(lock.scheduleId, lock.lockedDate, lock.lockedTime)
          if (slot && !slot.lockUsers.some((u) => u.id === lock.userId)) {
            slot.lockUsers.push({ id: lock.userId, name: '' })
            mutated = true
          }
        }
        if (mutated) {
          this.buildView()
          this.cdr.markForCheck()
        }
      })
    )

    this.socketSubscription.add(
      this.scheduleSocket.unlockedTimeSlotsUpdated$.subscribe((data: LockedTimeSlotType[]) => {
        let mutated = false
        for (const lock of data) {
          const slot = this.findSlotByDateTime(lock.scheduleId, lock.lockedDate, lock.lockedTime)
          if (slot) {
            const before = slot.lockUsers.length
            slot.lockUsers = slot.lockUsers.filter((u) => u.id !== lock.userId)
            if (slot.lockUsers.length !== before) mutated = true
          }
        }
        if (mutated) {
          this.buildView()
          this.cdr.markForCheck()
        }
      })
    )
  }

  // Locates a slot by (scheduleId, ISO date, HH:MM) — used to flag locked-by-other markers.
  private findSlotByDateTime(
    scheduleId: number,
    date: string,
    time: string
  ): CalendarSlot | undefined {
    return this.calendarSlots.find((s) => {
      if (s.scheduleId !== scheduleId) return false
      const startDate = s.start.split('T')[0]
      const startTime = s.start.split('T')[1]?.substring(0, 5)
      return startDate === date && startTime === time
    })
  }

  // Applies a remote transfer event in-memory when possible. Falls back to a refetch when
  // an appointment in the payload isn't loaded yet (e.g. it just landed on a schedule that
  // is now visible in this view).
  private applyTransferredEvent(event: AppointmentTransferredEvent): void {
    let needsRefetch = false
    let hasChanges = false

    for (const scheduling of event.schedulings) {
      const appointment = this.calendarSlots.find(
        (s): s is AppointmentSlot => s.type === 'appointment' && s.id === scheduling.appointmentId
      )
      if (!appointment) {
        const isDestinationVisible = this.schedules.some(
          (s) => s.id === scheduling.destinationScheduleId
        )
        if (isDestinationVisible) needsRefetch = true
        continue
      }

      const newStart = `${scheduling.date}T${scheduling.startTime}:00`
      const alreadyApplied =
        appointment.scheduleId === scheduling.destinationScheduleId &&
        appointment.start === newStart
      if (alreadyApplied) continue

      const oldStart = appointment.start
      const oldEnd = appointment.end
      const durationMs = this.domain.toMs(oldEnd) - this.domain.toMs(oldStart)
      const newEnd = this.domain.toLocalIso(this.domain.toMs(newStart) + durationMs)

      const moveData: AgendaEventMove = {
        appointment,
        oldScheduleId: scheduling.sourceScheduleId,
        newScheduleId: scheduling.destinationScheduleId,
        oldStart,
        oldEnd,
        newStart,
        newEnd,
      }
      hasChanges = true
      this.calendarSlots = this.domain.applyMove(moveData, this.calendarContext)
    }

    if (needsRefetch) {
      this.reloadRequest.emit()
    } else if (hasChanges) {
      this.buildView()
      this.cdr.markForCheck()
    }
  }

  private extractObservation(slot: CalendarSlot): string | undefined {
    if (slot.type === 'free') return (slot as FreeSlot).observation
    if (slot.type === 'blocked') return (slot as BlockedSlot).observation
    return undefined
  }

  private extractServices(slot: CalendarSlot): { id: number; name: string }[] | undefined {
    if (slot.type === 'free') return (slot as FreeSlot).services
    return undefined
  }

  protected servicesLabel(services?: { id: number; name: string }[]): string {
    return services?.map((s) => s.name).join(', ') ?? ''
  }

  private toTime(iso: string): string {
    const t = iso.split('T')[1]
    if (t) return t.substring(0, 5)
    const d = new Date(iso)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  protected statusColor(slot: AppointmentSlot): string {
    return getStatusColor(slot.status?.id)
  }

  /** Status name shown in tooltips/aria-labels next to the status dot. */
  protected statusName(slot: AppointmentSlot): string {
    return slot.status?.name ?? ''
  }

  protected procedureName(slot: AppointmentSlot): string {
    return slot.procedures?.map((p) => p.name).join(', ') ?? ''
  }

  protected showObservation(slot: AppointmentSlot): boolean {
    return slot.status?.id === 6 && !!slot.observation
  }

  protected isLocked(slot: CalendarSlot): boolean {
    return slot.lockUsers.length > 0
  }

  protected isAppointmentDraggable(slot: AppointmentSlot): boolean {
    // Drag is allowed when the user has at least ONE of fit-in / transfer — the per-drop
    // permission check inside onDrop decides whether the specific landing is allowed.
    if (!this.allowTransfer && !this.allowFitIn) return false
    if (slot.lockUsers.length > 0) return false
    const statusId = slot.status?.id
    return statusId != null && this.draggableStatusIds.includes(statusId)
  }

  protected isFullHour(time: string): boolean {
    return time.endsWith(':00')
  }

  protected formatTime(iso: string): string {
    return this.domain.formatTime(iso)
  }

  private resolveScheduleName(scheduleId: number): string {
    return this.schedules.find((s) => s.id === scheduleId)?.name ?? '—'
  }

  // For grouped cards: shows the time span from the earliest start to the latest start.
  // Falls back to the single startTime when the group has only one item (shouldn't happen).
  protected groupTimeRange(cell: DenseCellSlot): string {
    if (cell.type !== 'group' || !cell.groupSlots || cell.groupSlots.length === 0) {
      return cell.startTime
    }
    const first = cell.groupSlots[0]
    const last = cell.groupSlots[cell.groupSlots.length - 1]
    const start = this.domain.formatTime(first.start)
    const end = this.domain.formatTime(last.start)
    return start === end ? start : `${start} — ${end}`
  }

  protected onCardClick(cell: DenseCellSlot): void {
    // Locked appointments open the drawer directly; the unlock step is handled by the
    // "Destrancar horário e ir para o agendamento" button inside the drawer.
    if (cell.type !== 'appointment' && this.isLockedByOther(cell.slot)) {
      if (this.handleLockedClick(cell)) return
    }
    this.runCardAction(cell)
  }

  protected canCreateInCell(column: DenseColumn, row: DenseRow): boolean {
    if (!this.allowScheduling) return false
    if (!this.allowFitIn) return false
    return row.cellsByKey[column.key].length === 0
  }

  protected onEmptyCellClick(column: DenseColumn, row: DenseRow): void {
    if (!this.allowScheduling) return
    if (!this.allowFitIn) return
    this.navigateToNewSchedule(column.date, column.scheduleId, row.time)
  }

  private navigateToNewSchedule(dateInput: string, scheduleId?: number, time?: string): void {
    const date = dateInput.includes('T') ? dateInput.split('T')[0] : dateInput
    const hour =
      time ?? (dateInput.includes('T') ? dateInput.split('T')[1]?.substring(0, 5) : undefined)
    if (!hour || !scheduleId) return
    this.genexusService
      .getMultiScheduleNewServiceUrl(date, hour, String(scheduleId))
      .subscribe((url) => {
        this.genexusService.navigateTop(url)
      })
  }

  private runCardAction(cell: DenseCellSlot): void {
    if (cell.type === 'appointment') {
      // Appointment clicks open the LOCAL details drawer; we don't emit eventClick to avoid
      // double-handling at the parent (the parent's modal would also try to open).
      this.openDetailsDrawer = {
        appointment: cell.slot as AppointmentSlot,
        fromGroupKey: null,
      }
      this.cdr.markForCheck()
    } else if (cell.type === 'free') {
      if (!this.allowScheduling) return
      this.navigateToNewSchedule(cell.slot.start, cell.slot.scheduleId)
    } else if (cell.type === 'blocked') {
      this.eventClick.emit({ type: 'blocked', data: cell.slot })
    }
  }

  protected isLockedByOther(slot: CalendarSlot): boolean {
    if (slot.lockUsers.length === 0) return false
    const currentUserId = this.authService.decryptedData?.user_id
    return slot.lockUsers.some((u) => u.id !== currentUserId)
  }

  // Returns true when the caller should stop (first click in the 3s window, or a
  // click inside the post-force-entry cooldown). Returns false when the caller
  // should proceed with the normal action — that path also triggers the audit.
  private handleLockedClick(cell: DenseCellSlot): boolean {
    const now = Date.now()
    const eventKey = `${cell.slot.scheduleId}-${cell.slot.start}`

    if (
      this.recentForceEntry &&
      this.recentForceEntry.eventKey === eventKey &&
      now - this.recentForceEntry.time < DenseScheduleGridComponent.FORCE_ENTRY_COOLDOWN_MS
    ) {
      return true
    }

    if (
      this.lastLockedClick &&
      this.lastLockedClick.eventKey === eventKey &&
      now - this.lastLockedClick.time < 3000
    ) {
      this.lastLockedClick = null
      this.recentForceEntry = { eventKey, time: now }
      this.registerForceEntryAudit(cell.slot)
      return false
    }

    this.lastLockedClick = { eventKey, time: now }
    this.toastService.openToast(
      'informação',
      'Este horário está sendo utilizado por outro usuário. Clique duas vezes para forçar a entrada.'
    )
    return true
  }

  private registerForceEntryAudit(slot: CalendarSlot): void {
    const date = slot.start.split('T')[0]
    const time = slot.start.split('T')[1]?.substring(0, 5)
    this.lockedTimeSlotService.createForceEntryAudit(slot.scheduleId, date, time).subscribe({
      error: () =>
        this.toastService.openToast('informação', 'Não foi possível registrar a entrada forçada'),
    })
  }

  protected onHistoryButtonClick(event: Event, slot: AppointmentSlot): void {
    event.stopPropagation()
    this.historyClick.emit(slot)
  }

  // --- Grouping handlers ---

  protected onGroupCardClick(cell: DenseCellSlot, column: DenseColumn): void {
    if (cell.type !== 'group' || !cell.groupSlots || !cell.groupKey) return
    if (this.isLockedByOther(cell.slot)) {
      if (this.handleLockedClick(cell)) return
    }
    this.openGroupDrawer = {
      groupKey: cell.groupKey,
      appointments: cell.groupSlots,
      column,
    }
    this.cdr.markForCheck()
  }

  protected onCloseGroupDrawer(): void {
    this.openGroupDrawer = null
    this.cdr.markForCheck()
  }

  // Toggle ungroup from a group card (chain-link icon → break it).
  protected onUngroupClick(event: Event, cell: DenseCellSlot): void {
    event.stopPropagation()
    if (cell.type !== 'group' || !cell.groupKey) return
    this.ungroupedKeys.add(cell.groupKey)
    this.buildView()
    this.cdr.markForCheck()
  }

  // Toggle group from an individual card with groupable neighbors (broken-chain icon → join).
  protected onGroupClick(event: Event, cell: DenseCellSlot): void {
    event.stopPropagation()
    if (cell.type !== 'appointment') return
    // Clear all session-level ungrouping flags so any appointment in this bucket that was
    // previously detached or had its group key suppressed becomes eligible again. Clicking the
    // chain on ANY card in the cell should restore the full group, regardless of which one is
    // the still-detached neighbor.
    this.detachedAppointmentIds.clear()
    this.ungroupedKeys.clear()
    this.buildView()
    this.cdr.markForCheck()
  }

  // Inside the drawer: pull a single appointment OUT of the group.
  protected onRemoveFromGroup(slot: AppointmentSlot): void {
    this.detachedAppointmentIds.add(slot.id)
    if (this.openGroupDrawer) {
      // Remove from the in-flight drawer list so it updates without closing.
      this.openGroupDrawer = {
        ...this.openGroupDrawer,
        appointments: this.openGroupDrawer.appointments.filter((a) => a.id !== slot.id),
      }
      // If only 0 or 1 left, the group will dissolve automatically; close the drawer.
      if (this.openGroupDrawer.appointments.length < 2) {
        this.openGroupDrawer = null
      }
    }
    this.buildView()
    this.cdr.markForCheck()
  }

  // Inside the drawer: ungroup all (preserves all appointments as individuals).
  protected onUngroupFromDrawer(): void {
    if (!this.openGroupDrawer) return
    this.ungroupedKeys.add(this.openGroupDrawer.groupKey)
    this.openGroupDrawer = null
    this.buildView()
    this.cdr.markForCheck()
  }

  // Drawer item click (from inside the group drawer) → swap into the appointment details
  // view inside the same drawer panel, stashing the group state so the user can hit "Voltar".
  protected onDrawerItemClick(slot: AppointmentSlot): void {
    if (this.openGroupDrawer) {
      this.stashedGroupDrawer = this.openGroupDrawer
      this.openGroupDrawer = null
    }
    this.openDetailsDrawer = {
      appointment: slot,
      fromGroupKey: this.stashedGroupDrawer?.groupKey ?? null,
    }
    this.cdr.markForCheck()
  }

  // Back button from details → restore the previously-shown group drawer.
  protected get detailsBreadcrumbItems(): OcBreadcrumbItem[] {
    if (!this.openDetailsDrawer?.fromGroupKey) return []
    return [
      { label: 'Agendamentos do grupo', route: [] },
      { label: `Agendamento #${this.openDetailsDrawer.appointment.id}` },
    ]
  }

  protected onDetailsBack(): void {
    this.openDetailsDrawer = null
    if (this.stashedGroupDrawer) {
      this.openGroupDrawer = this.stashedGroupDrawer
      this.stashedGroupDrawer = null
    }
    this.cdr.markForCheck()
  }

  // X / overlay close from details → close everything (drawer + any stashed group context).
  protected onDetailsClose(): void {
    this.openDetailsDrawer = null
    this.stashedGroupDrawer = null
    this.cdr.markForCheck()
  }

  // Forwarded actions from the shared appointment-details component.
  protected onDetailsHistoryClick(slot: AppointmentSlot): void {
    this.historyClick.emit(slot)
    this.onDetailsClose()
  }

  protected onDetailsOpenAppointmentClick(slot: AppointmentSlot): void {
    // When the slot is locked by another user, drop the lock before the parent navigates to
    // GX — otherwise the user would land on the appointment still flagged as in-use.
    if (this.isLockedByOther(slot)) {
      this.unlockTimeSlot(slot.start, slot.scheduleId)
    }
    this.openAppointmentClick.emit(slot)
    this.onDetailsClose()
  }

  // --- HTML5 drag-and-drop ---

  protected onDragStart(event: DragEvent, slot: AppointmentSlot): void {
    if (!this.isAppointmentDraggable(slot)) {
      event.preventDefault()
      return
    }
    event.dataTransfer?.setData('text/plain', String(slot.id))
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setDragImage(DenseScheduleGridComponent.TRANSPARENT_DRAG_IMAGE, 0, 0)
    }
    this.draggingApptId = slot.id
    this.draggingGroup = null
    this.installDragPreview(event.currentTarget as HTMLElement, event.clientX, event.clientY)
    this.beginDragLock([{ start: slot.start, scheduleId: slot.scheduleId }])
    this.startAutoScroll()
  }

  protected onGroupDragStart(event: DragEvent, cell: DenseCellSlot): void {
    if (cell.type !== 'group' || !cell.groupSlots || cell.groupSlots.length < 2) {
      event.preventDefault()
      return
    }
    // Use the representative (earliest) appointment to honor isAppointmentDraggable rules.
    const representative = cell.slot as AppointmentSlot
    if (!this.isAppointmentDraggable(representative)) {
      event.preventDefault()
      return
    }
    event.dataTransfer?.setData('text/plain', `group:${cell.groupKey ?? ''}`)
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setDragImage(DenseScheduleGridComponent.TRANSPARENT_DRAG_IMAGE, 0, 0)
    }
    this.draggingApptId = representative.id
    this.draggingGroup = [...cell.groupSlots]
    this.installDragPreview(event.currentTarget as HTMLElement, event.clientX, event.clientY)
    this.beginDragLock(cell.groupSlots.map((s) => ({ start: s.start, scheduleId: s.scheduleId })))
    this.startAutoScroll()
  }

  protected onDragEnd(): void {
    this.draggingApptId = null
    this.draggingGroup = null
    this.dragOverKey = null
    this.dragOverTime = null
    this.dropPreview = null
    this.removeDragPreview()
    this.scheduleUnlockDebounce()
    this.stopAutoScroll()
    this.cdr.markForCheck()
  }

  // --- Custom drag preview ---
  //
  // We clone the source card so the floating ghost looks identical to the card the user
  // grabbed (whole card, full content). The native ghost is suppressed with a transparent
  // image because its rendering can be off at non-100% zoom / high-DPI. The cloned `.dense-card__time`
  // span is updated from `dragOverTime` in onScrollContainerDragOver so the user sees the
  // drop target's time while dragging.

  private installDragPreview(sourceEl: HTMLElement, clientX: number, clientY: number): void {
    this.removeDragPreview()
    if (!sourceEl) return

    const rect = sourceEl.getBoundingClientRect()
    const clone = sourceEl.cloneNode(true) as HTMLElement
    // Strip the --dragging modifier on the clone — that class fades the source to 0.4 opacity,
    // and we want the floating ghost fully visible.
    clone.classList.remove('dense-card--dragging')
    clone.style.cssText = [
      `position:fixed`,
      `top:0`,
      `left:0`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      `z-index:9999`,
      `pointer-events:none`,
      `opacity:0.92`,
      `box-shadow:0 8px 20px rgba(0,0,0,0.25)`,
      `transform:translate3d(-9999px,-9999px,0)`,
      `will-change:transform`,
      `margin:0`,
    ].join(';')

    // Locate the time span inside the clone so we can live-update it on hover.
    const timeEl = clone.querySelector<HTMLSpanElement>('.dense-card__time')
    // Anchor the clone so the cursor stays at the same offset within the card where it
    // started — feels more natural than always pinning the top-left to the cursor.
    const offsetX = clientX - rect.left
    const offsetY = clientY - rect.top

    document.body.appendChild(clone)
    this.dragPreviewEl = clone
    this.dragPreviewTimeEl = timeEl
    this.dragPreviewLastTime = timeEl?.textContent ?? null
    this.dragPreviewOffsetX = offsetX
    this.dragPreviewOffsetY = offsetY
    this.updateDragPreview(clientX, clientY)
  }

  private updateDragPreview(clientX: number, clientY: number): void {
    if (!this.dragPreviewEl) return
    // Keep the grab point under the cursor (matches where the user picked up the card).
    const x = clientX - this.dragPreviewOffsetX
    const y = clientY - this.dragPreviewOffsetY
    this.dragPreviewEl.style.transform = `translate3d(${x}px, ${y}px, 0)`
    if (this.dragPreviewTimeEl) {
      const next = this.dragOverTime
      if (next && next !== this.dragPreviewLastTime) {
        this.dragPreviewTimeEl.textContent = next
        this.dragPreviewLastTime = next
      }
    }
  }

  private removeDragPreview(): void {
    if (this.dragPreviewEl?.parentNode) {
      this.dragPreviewEl.parentNode.removeChild(this.dragPreviewEl)
    }
    this.dragPreviewEl = null
    this.dragPreviewTimeEl = null
    this.dragPreviewLastTime = null
  }

  // --- Drag-lock helpers (mirror agenda-calendar's lock/unlock flow so the dense view
  // participates in the same cross-client lock protocol via LockedTimeSlotService).

  private beginDragLock(slots: { start: string; scheduleId: number }[]): void {
    this.cancelUnlockDebounce()
    this.draggingLockedSlots = [...slots]
    for (const slot of slots) {
      this.lockTimeSlot(slot.start, slot.scheduleId, 'DRAGGING')
    }
  }

  private scheduleUnlockDebounce(): void {
    if (!this.draggingLockedSlots.length) return
    const slots = [...this.draggingLockedSlots]
    this.unlockDebounceTimer = setTimeout(() => {
      for (const slot of slots) {
        this.unlockTimeSlot(slot.start, slot.scheduleId)
      }
      this.draggingLockedSlots = []
      this.unlockDebounceTimer = null
    }, 3000)
  }

  private cancelUnlockDebounce(): void {
    if (this.unlockDebounceTimer) {
      clearTimeout(this.unlockDebounceTimer)
      this.unlockDebounceTimer = null
    }
  }

  // Called by the parent after a successful/failed transfer to release the lock immediately
  // (and cancel the pending debounce so we don't issue a redundant unlock).
  unlockDraggingSlot(): void {
    this.cancelUnlockDebounce()
    if (!this.draggingLockedSlots.length) return
    for (const slot of this.draggingLockedSlots) {
      this.unlockTimeSlot(slot.start, slot.scheduleId)
    }
    this.draggingLockedSlots = []
  }

  private lockTimeSlot(start: string, scheduleId: number, reason?: string): void {
    const date = start.split('T')[0]
    const time = start.split('T')[1]?.substring(0, 5)
    this.lockedTimeSlotService
      .lockTimeSlot({ date, time, scheduleId, urgentCareId: null, reason })
      .subscribe({
        error: () =>
          this.toastService.openToast('informação', 'Não foi possível bloquear o horário'),
      })
  }

  private unlockTimeSlot(start: string, scheduleId: number): void {
    const date = start.split('T')[0]
    const time = start.split('T')[1]?.substring(0, 5)
    this.lockedTimeSlotService.deleteLockedTimeSlot(scheduleId, date, time).subscribe({
      error: () =>
        this.toastService.openToast('informação', 'Não foi possível desbloquear o horário'),
    })
  }

  protected onScrollContainerDragOver(event: DragEvent): void {
    if (this.draggingApptId == null) return
    this.autoScrollPointerY = event.clientY
    this.updateDragPreview(event.clientX, event.clientY)
  }

  private startAutoScroll(): void {
    if (this.autoScrollRafId != null) return
    const tick = () => {
      const container = this.scrollContainer?.nativeElement
      const pointerY = this.autoScrollPointerY
      if (!container || pointerY == null) {
        this.autoScrollRafId = requestAnimationFrame(tick)
        return
      }
      const rect = container.getBoundingClientRect()
      const edge = DenseScheduleGridComponent.AUTO_SCROLL_EDGE_PX
      const max = DenseScheduleGridComponent.AUTO_SCROLL_MAX_SPEED_PX
      const distFromTop = pointerY - rect.top
      const distFromBottom = rect.bottom - pointerY
      let delta = 0
      if (distFromTop < edge && distFromTop >= 0) {
        delta = -Math.ceil(((edge - distFromTop) / edge) * max)
      } else if (distFromBottom < edge && distFromBottom >= 0) {
        delta = Math.ceil(((edge - distFromBottom) / edge) * max)
      }
      if (delta !== 0) container.scrollTop += delta
      this.autoScrollRafId = requestAnimationFrame(tick)
    }
    this.autoScrollRafId = requestAnimationFrame(tick)
  }

  private stopAutoScroll(): void {
    if (this.autoScrollRafId != null) {
      cancelAnimationFrame(this.autoScrollRafId)
      this.autoScrollRafId = null
    }
    this.autoScrollPointerY = null
  }

  protected onDragOver(event: DragEvent, columnKey: string, time: string): void {
    if (this.draggingApptId == null) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'

    const cellChanged = this.dragOverKey !== columnKey || this.dragOverTime !== time
    if (cellChanged) {
      this.dragOverKey = columnKey
      this.dragOverTime = time
    }

    // Compute drop indicator position relative to the card the mouse is closest to.
    // The cell element is the currentTarget; its children include the rendered cards.
    // Free cards are excluded — when the cell only has free slots the whole cell highlights
    // via the `--drop-on-free` modifier instead of showing the line indicator.
    const cellEl = event.currentTarget as HTMLElement | null
    const mouseY = event.clientY
    let next: typeof this.dropPreview = null
    if (cellEl) {
      const cardEls = Array.from(
        cellEl.querySelectorAll<HTMLElement>(':scope > .dense-card:not(.dense-card--free)')
      )
      if (cardEls.length > 0) {
        let bestIdx = 0
        let bestDist = Number.POSITIVE_INFINITY
        let bestPosition: 'above' | 'below' = 'above'
        for (let i = 0; i < cardEls.length; i++) {
          const rect = cardEls[i].getBoundingClientRect()
          const middle = rect.top + rect.height / 2
          const dist = Math.abs(mouseY - middle)
          if (dist < bestDist) {
            bestDist = dist
            bestIdx = i
            bestPosition = mouseY < middle ? 'above' : 'below'
          }
        }
        next = { columnKey, rowTime: time, anchorIndex: bestIdx, position: bestPosition }
      }
    }

    const previewChanged =
      next?.columnKey !== this.dropPreview?.columnKey ||
      next?.rowTime !== this.dropPreview?.rowTime ||
      next?.anchorIndex !== this.dropPreview?.anchorIndex ||
      next?.position !== this.dropPreview?.position

    if (cellChanged || previewChanged) {
      this.dropPreview = next
      this.cdr.markForCheck()
    }
  }

  protected onDragLeave(): void {
    // Highlight clears in onDragEnd; intentionally no-op to avoid flicker between cells.
  }

  // Decides where the dropped card should land in the target cell.
  //
  //   - Empty cell or no anchor                     → newStart = row.time, no shift.
  //   - Above the FIRST card in the cell            → newStart = first.start - 1min, no shift.
  //   - Below the LAST card                         → newStart = last.start + 1min, no shift.
  //   - In the middle (above card N>0 OR below N<last) → newStart = above-card.start + 1min,
  //     and every card from index N onward (excluding the moving one) shifts +1min.
  //
  // bucketCells excludes the dragged card from the "siblings" so it doesn't conflict with itself.
  // shiftFromIndex points into bucketCells (NOT into the visual list).
  private resolvePlacement(
    column: DenseColumn,
    time: string,
    moving: AppointmentSlot,
    preview: typeof this.dropPreview
  ): {
    newStart: string
    bucketCells: AppointmentSlot[]
    shiftFromIndex: number | null
  } {
    const fallback = `${column.date}T${time}:00`

    // Find the row + cells in the target cell. The view orders cells chronologically already.
    const row = this.rows.find((r) => r.time === time)
    if (!row || !preview || preview.columnKey !== column.key || preview.rowTime !== time) {
      return { newStart: fallback, bucketCells: [], shiftFromIndex: null }
    }

    const cells = row.cellsByKey[column.key] ?? []
    // Flatten group cards into their underlying appointments — the cascade is per-appointment,
    // not per-visual-card. Detached individuals already render as 'appointment' cells.
    const orderedAppointments: AppointmentSlot[] = []
    for (const c of cells) {
      if (c.type === 'appointment') {
        orderedAppointments.push(c.slot as AppointmentSlot)
      } else if (c.type === 'group' && c.groupSlots) {
        orderedAppointments.push(...c.groupSlots)
      }
    }
    // Exclude the moving appointment itself from siblings.
    const siblings = orderedAppointments.filter((a) => a.id !== moving.id)
    if (siblings.length === 0) {
      return { newStart: fallback, bucketCells: [], shiftFromIndex: null }
    }

    // The anchor index in the visual cells doesn't necessarily match the appointment index
    // (groups collapse multiple). So translate by mapping the anchor card to its first
    // appointment in the flattened list — close enough for above/below decisions.
    const anchorVisual = cells[preview.anchorIndex]
    let anchorApt: AppointmentSlot | null = null
    if (anchorVisual) {
      if (anchorVisual.type === 'appointment') {
        anchorApt = anchorVisual.slot as AppointmentSlot
      } else if (anchorVisual.type === 'group' && anchorVisual.groupSlots?.length) {
        anchorApt =
          preview.position === 'above'
            ? anchorVisual.groupSlots[0]
            : anchorVisual.groupSlots[anchorVisual.groupSlots.length - 1]
      }
    }
    if (!anchorApt) {
      return { newStart: fallback, bucketCells: siblings, shiftFromIndex: null }
    }

    const anchorIdxInSiblings = siblings.findIndex((a) => a.id === anchorApt!.id)
    if (anchorIdxInSiblings < 0) {
      return { newStart: fallback, bucketCells: siblings, shiftFromIndex: null }
    }

    const anchorMs = this.domain.toMs(anchorApt.start)

    if (preview.position === 'above') {
      const newStartMs = anchorMs - 60_000
      const newStart = this.domain.toLocalIso(newStartMs)
      // No shift: the moving card just slips above without pushing anyone (it inserts BEFORE).
      // Exception: if the cursor was above an inner card (not the first), inserting the new
      // appointment at anchor-1 collides with the previous sibling. The user-stated rule says
      // shift the subsequent ones, NOT the previous ones. So no cascade above-of-first-card,
      // and no cascade above-of-inner — anchor.start - 1min is the new appointment's start
      // and the user accepts the possible collision (handled downstream by domain).
      return { newStart, bucketCells: siblings, shiftFromIndex: null }
    }

    // position === 'below'
    const newStartMs = anchorMs + 60_000
    const newStart = this.domain.toLocalIso(newStartMs)
    // Cascade applies when there is at least one sibling AFTER the anchor that would now
    // overlap or collide with the new card.
    const firstAfter = siblings.findIndex((a, idx) => idx > anchorIdxInSiblings)
    if (firstAfter < 0) {
      // Anchor is the last sibling — drop just goes below. No shift.
      return { newStart, bucketCells: siblings, shiftFromIndex: null }
    }
    return { newStart, bucketCells: siblings, shiftFromIndex: firstAfter }
  }

  // Classifies the destination cell at (column, time) into one of three flavors used by the
  // drop logic to decide between fit-in vs regular move.
  //
  //   - 'has-cards' : at least one appointment / group / blocked / continuation card lives there.
  //                   Drop becomes a fit-in (encaixe) directly.
  //   - 'free-only' : the cell only contains free slot card(s). Drop is a regular move and
  //                   passes through the domain validations.
  //   - 'empty'     : nothing rendered (shows the bare row time). Treated as regular move with
  //                   user confirmation, since dropping in a blank cell is less obvious.
  private classifyDropTarget(
    column: DenseColumn,
    time: string
  ): 'has-cards' | 'free-only' | 'empty' {
    const row = this.rows.find((r) => r.time === time)
    if (!row) return 'empty'
    const cells = row.cellsByKey[column.key] ?? []
    if (cells.length === 0) return 'empty'
    const hasNonFree = cells.some((c) => c.type !== 'free')
    return hasNonFree ? 'has-cards' : 'free-only'
  }

  // True when the user is currently dragging over a cell at this column+row whose only
  // content is a free slot. Used to highlight the free card in purple instead of showing
  // the "above/below another card" line indicator.
  protected isDropOnFreeCell(columnKey: string, rowTime: string): boolean {
    if (this.draggingApptId == null) return false
    if (this.dragOverKey !== columnKey || this.dragOverTime !== rowTime) return false
    const row = this.rows.find((r) => r.time === rowTime)
    if (!row) return false
    const cells = row.cellsByKey[columnKey] ?? []
    if (cells.length === 0) return false
    return cells.every((c) => c.type === 'free')
  }

  protected onDrop(event: DragEvent, column: DenseColumn, time: string): void {
    event.preventDefault()
    const apptId = this.draggingApptId
    const droppedGroup = this.draggingGroup
    const preview = this.dropPreview
    this.draggingApptId = null
    this.draggingGroup = null
    this.dragOverKey = null
    this.dragOverTime = null
    this.dropPreview = null
    this.stopAutoScroll()

    if (apptId == null) return

    // Group drop: ask the user whether to move all or pick some.
    if (droppedGroup && droppedGroup.length >= 2) {
      const groupCrossSchedule = droppedGroup.some((a) => a.scheduleId !== column.scheduleId)
      if (groupCrossSchedule && !this.allowTransfer) {
        this.toastService.openToast(
          'informação',
          'Você não tem permissão para realizar transferências'
        )
        this.cdr.markForCheck()
        return
      }
      this.pendingGroupMove = {
        appointments: droppedGroup,
        targetColumn: column,
        targetTime: time,
        selectedIds: new Set(droppedGroup.map((a) => a.id)),
        mode: 'choose',
      }
      this.cdr.markForCheck()
      return
    }

    const appointment = this.calendarSlots.find(
      (s): s is AppointmentSlot => s.type === 'appointment' && s.id === apptId
    )
    if (!appointment) return

    const durationMs = this.domain.toMs(appointment.end) - this.domain.toMs(appointment.start)

    // Resolve newStart based on the drop preview (anchor card +/- 1min) or row time.
    const placement = this.resolvePlacement(column, time, appointment, preview)
    const newStart = placement.newStart
    const newEnd = this.domain.toLocalIso(this.domain.toMs(newStart) + durationMs)
    const target = this.classifyDropTarget(column, time)

    // Cascade shift: drop between cards causes subsequent cards in the same cell to shift +1min.
    // The dropped card lands as a FIT-IN (cards in the cell ⇒ has-cards target). The shifted
    // neighbors stay as regular moves (they continue to be normal appointments at new times).
    if (placement.shiftFromIndex !== null && placement.bucketCells.length > 0) {
      const movesToShift: AgendaEventMove[] = []
      for (let i = placement.shiftFromIndex; i < placement.bucketCells.length; i++) {
        const apt = placement.bucketCells[i]
        if (apt.id === appointment.id) continue
        const aptDurationMs = this.domain.toMs(apt.end) - this.domain.toMs(apt.start)
        const shiftedStartMs = this.domain.toMs(apt.start) + 60_000
        const shiftedStart = this.domain.toLocalIso(shiftedStartMs)
        const shiftedEnd = this.domain.toLocalIso(shiftedStartMs + aptDurationMs)
        movesToShift.push({
          appointment: apt,
          oldScheduleId: apt.scheduleId,
          newScheduleId: apt.scheduleId,
          oldStart: apt.start,
          oldEnd: apt.end,
          newStart: shiftedStart,
          newEnd: shiftedEnd,
        })
      }
      if (movesToShift.length > 0) {
        if (!this.allowFitIn) {
          this.toastService.openToast('informação', 'Você não tem permissão para realizar encaixes')
          this.cdr.markForCheck()
          return
        }
        this.pendingShiftMove = {
          movingAppointment: appointment,
          newStart,
          newEnd,
          targetColumn: column,
          shiftMoves: movesToShift,
        }
        this.cdr.markForCheck()
        return
      }
    }

    const isCrossSchedule = appointment.scheduleId !== column.scheduleId
    if (isCrossSchedule && !this.allowTransfer) {
      this.toastService.openToast(
        'informação',
        'Você não tem permissão para realizar transferências'
      )
      this.cdr.markForCheck()
      return
    }

    // Has-cards target: the cell already holds at least one non-free card. Dropping here means
    // squeezing a fit-in into an occupied slot, so we ask the user to confirm before applying.
    if (target === 'has-cards') {
      if (!this.allowFitIn) {
        this.toastService.openToast('informação', 'Você não tem permissão para realizar encaixes')
        this.cdr.markForCheck()
        return
      }
      const totalMinutes = durationMs / 60_000
      const moveData: AgendaEventMove = {
        appointment,
        oldScheduleId: appointment.scheduleId,
        newScheduleId: column.scheduleId,
        oldStart: appointment.start,
        oldEnd: appointment.end,
        newStart,
        newEnd,
      }
      this.pendingMove = {
        moveData,
        isFitIn: true,
        patientName: appointment.patient?.name ?? '—',
        sourceTime: this.domain.formatTime(appointment.start),
        sourceScheduleName: this.resolveScheduleName(appointment.scheduleId),
        targetTime: this.domain.formatTime(newStart),
        targetScheduleName: this.resolveScheduleName(column.scheduleId),
        coveredMinutes: 0,
        totalMinutes,
      }
      this.cdr.markForCheck()
      return
    }

    // Empty target: ask for confirmation before moving — there's no reference card to show
    // the user this is intentional, so we surface a modal to acknowledge the new time.
    if (target === 'empty') {
      if (!this.allowFitIn) {
        this.toastService.openToast('informação', 'Você não tem permissão para realizar encaixes')
        this.cdr.markForCheck()
        return
      }
      const totalMinutes = durationMs / 60_000
      const moveData: AgendaEventMove = {
        appointment,
        oldScheduleId: appointment.scheduleId,
        newScheduleId: column.scheduleId,
        oldStart: appointment.start,
        oldEnd: appointment.end,
        newStart,
        newEnd,
      }
      this.pendingMove = {
        moveData,
        isFitIn: false,
        patientName: appointment.patient?.name ?? '—',
        sourceTime: this.domain.formatTime(appointment.start),
        sourceScheduleName: this.resolveScheduleName(appointment.scheduleId),
        targetTime: this.domain.formatTime(newStart),
        targetScheduleName: this.resolveScheduleName(column.scheduleId),
        coveredMinutes: totalMinutes,
        totalMinutes,
      }
      this.cdr.markForCheck()
      return
    }

    // free-only target: regular move through the domain, with all its validations.
    const result = this.domain.moveSlot(
      appointment,
      appointment.scheduleId,
      column.scheduleId,
      newStart,
      newEnd,
      this.calendarContext
    )
    if (result.action === 'blocked') {
      this.toastService.openToast('informação', result.error)
      this.cdr.markForCheck()
      return
    }
    if (result.action === 'moved') {
      const totalMinutes = durationMs / 60_000
      this.pendingMove = {
        moveData: result.moveData,
        isFitIn: false,
        isCleanMove: true,
        patientName: appointment.patient?.name ?? '—',
        sourceTime: this.domain.formatTime(appointment.start),
        sourceScheduleName: this.resolveScheduleName(appointment.scheduleId),
        targetTime: this.domain.formatTime(result.moveData.newStart),
        targetScheduleName: this.resolveScheduleName(column.scheduleId),
        coveredMinutes: totalMinutes,
        totalMinutes,
      }
      this.cdr.markForCheck()
      return
    }
    // The domain may need confirmation (e.g. duration/snap) or claim it's blocked. In both
    // cases we trust the user's explicit drop on the free cell and apply the move with the
    // chosen newStart, treating it as fit-in if it can't be a clean move.
    const totalMinutes = durationMs / 60_000
    const fallbackMoveData: AgendaEventMove =
      result.action === 'needs-confirmation'
        ? result.moveData
        : {
            appointment,
            oldScheduleId: appointment.scheduleId,
            newScheduleId: column.scheduleId,
            oldStart: appointment.start,
            oldEnd: appointment.end,
            newStart,
            newEnd,
          }
    const isFitIn = result.action === 'needs-confirmation' ? !result.availableFreeSlot : true
    this.pendingMove = {
      moveData: fallbackMoveData,
      isFitIn,
      patientName: appointment.patient?.name ?? '—',
      sourceTime: this.domain.formatTime(appointment.start),
      sourceScheduleName: this.resolveScheduleName(appointment.scheduleId),
      targetTime: this.domain.formatTime(fallbackMoveData.newStart),
      targetScheduleName: this.resolveScheduleName(column.scheduleId),
      coveredMinutes: result.action === 'needs-confirmation' ? result.coveredMinutes : 0,
      totalMinutes: result.action === 'needs-confirmation' ? result.totalMinutes : totalMinutes,
    }
    this.cdr.markForCheck()
  }

  // --- Cascade shift move (drop between cards in same bucket) ---

  protected confirmPendingShiftMove(): void {
    if (!this.pendingShiftMove) return
    if (!this.allowFitIn) {
      this.pendingShiftMove = null
      this.toastService.openToast('informação', 'Você não tem permissão para realizar encaixes')
      this.cdr.markForCheck()
      return
    }
    const { movingAppointment, newStart, newEnd, targetColumn, shiftMoves } = this.pendingShiftMove
    this.pendingShiftMove = null

    const movingMove: AgendaEventMove = {
      appointment: movingAppointment,
      oldScheduleId: movingAppointment.scheduleId,
      newScheduleId: targetColumn.scheduleId,
      oldStart: movingAppointment.start,
      oldEnd: movingAppointment.end,
      newStart,
      newEnd,
      isFitIn: true,
    }
    const allMoves = [movingMove, ...shiftMoves]

    // Apply all moves optimistically in-memory.
    for (const m of allMoves) {
      this.calendarSlots = this.domain.applyMove(m, this.calendarContext, { markAsFitIn: true })
    }

    // 1 move → single endpoint; 2+ → batch.
    if (allMoves.length === 1) {
      this.eventMove.emit(allMoves[0])
    } else {
      this.eventMoveBatch.emit(allMoves)
    }

    this.buildView()
    this.cdr.markForCheck()
  }

  protected cancelPendingShiftMove(): void {
    this.pendingShiftMove = null
    this.cdr.markForCheck()
  }

  // --- Group move (drop on grouped card) ---

  protected chooseGroupMoveAll(): void {
    if (!this.pendingGroupMove) return
    const { appointments } = this.pendingGroupMove
    this.applyGroupMove(appointments)
  }

  protected enterGroupMovePickSome(): void {
    if (!this.pendingGroupMove) return
    this.pendingGroupMove = { ...this.pendingGroupMove, mode: 'pick-some' }
    this.cdr.markForCheck()
  }

  protected toggleGroupMoveSelection(id: number | string, checked: boolean): void {
    if (!this.pendingGroupMove) return
    const next = new Set(this.pendingGroupMove.selectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    this.pendingGroupMove = { ...this.pendingGroupMove, selectedIds: next }
    this.cdr.markForCheck()
  }

  protected isGroupMoveSelected(id: number | string): boolean {
    return !!this.pendingGroupMove?.selectedIds.has(id)
  }

  protected confirmGroupMovePickSome(): void {
    if (!this.pendingGroupMove) return
    const selected = this.pendingGroupMove.appointments.filter((a) =>
      this.pendingGroupMove!.selectedIds.has(a.id)
    )
    if (selected.length === 0) return
    this.applyGroupMove(selected)
  }

  protected cancelGroupMove(): void {
    this.pendingGroupMove = null
    this.cdr.markForCheck()
  }

  // Computes a moveData per appointment, applies optimistically, and emits either a single
  // eventMove or a batch eventMoveBatch depending on count.
  //
  // The earliest selected appointment lands exactly on the target time. Remaining appointments
  // keep their original offset relative to that earliest start (e.g. a group of 07:00/07:01/07:02
  // moved to 09:30 becomes 09:30/09:31/09:32).
  private applyGroupMove(appointments: AppointmentSlot[]): void {
    if (!this.pendingGroupMove || appointments.length === 0) return
    const { targetColumn, targetTime } = this.pendingGroupMove
    const groupCrossSchedule = appointments.some((a) => a.scheduleId !== targetColumn.scheduleId)
    if (groupCrossSchedule && !this.allowTransfer) {
      this.pendingGroupMove = null
      this.toastService.openToast(
        'informação',
        'Você não tem permissão para realizar transferências'
      )
      this.cdr.markForCheck()
      return
    }
    this.pendingGroupMove = null

    const sorted = [...appointments].sort(
      (a, b) => this.domain.toMs(a.start) - this.domain.toMs(b.start)
    )
    const anchorMs = this.domain.toMs(sorted[0].start)
    const targetAnchorMs = this.domain.toMs(`${targetColumn.date}T${targetTime}:00`)

    const moves: AgendaEventMove[] = []
    for (const apt of sorted) {
      const offsetMs = this.domain.toMs(apt.start) - anchorMs
      const durationMs = this.domain.toMs(apt.end) - this.domain.toMs(apt.start)
      const newStartMs = targetAnchorMs + offsetMs
      const newStart = this.domain.toLocalIso(newStartMs)
      const newEnd = this.domain.toLocalIso(newStartMs + durationMs)
      const moveData: AgendaEventMove = {
        appointment: apt,
        oldScheduleId: apt.scheduleId,
        newScheduleId: targetColumn.scheduleId,
        oldStart: apt.start,
        oldEnd: apt.end,
        newStart,
        newEnd,
      }
      moves.push(moveData)
      // Apply optimistically in-memory so the UI reflects the change immediately.
      this.calendarSlots = this.domain.applyMove(moveData, this.calendarContext)
    }

    if (moves.length === 1) {
      this.eventMove.emit(moves[0])
    } else if (moves.length > 1) {
      this.eventMoveBatch.emit(moves)
    }

    this.buildView()
    this.cdr.markForCheck()
  }

  protected confirmPendingMove(): void {
    if (!this.pendingMove) {
      return
    }
    const { moveData, isFitIn } = this.pendingMove
    if (this.pendingMoveTimeError) {
      return
    }
    const isCrossSchedule = moveData.oldScheduleId !== moveData.newScheduleId
    if (isCrossSchedule && !this.allowTransfer) {
      this.pendingMove = null
      this.pendingMoveTimeError = null
      this.toastService.openToast(
        'informação',
        'Você não tem permissão para realizar transferências'
      )
      this.cdr.markForCheck()
      return
    }
    if (isFitIn && !this.allowFitIn) {
      this.pendingMove = null
      this.pendingMoveTimeError = null
      this.toastService.openToast('informação', 'Você não tem permissão para realizar encaixes')
      this.cdr.markForCheck()
      return
    }
    this.pendingMove = null
    this.pendingMoveTimeError = null
    this.eventMove.emit({ ...moveData, isFitIn })
    this.calendarSlots = this.domain.applyMove(moveData, this.calendarContext, {
      markAsFitIn: isFitIn,
    })
    this.buildView()
    this.cdr.markForCheck()
  }

  protected cancelPendingMove(): void {
    this.pendingMove = null
    this.pendingMoveTimeError = null
    this.cdr.markForCheck()
  }

  protected onPendingMoveTargetTimeChange(time: string): void {
    if (!this.pendingMove) {
      return
    }

    if (!/^\d{2}:\d{2}$/.test(time)) {
      this.pendingMoveTimeError = this.getTranslation(1181)
      this.cdr.markForCheck()
      return
    }

    const [hour, minute] = time.split(':').map(Number)
    if (hour > 23 || minute > 59) {
      this.pendingMoveTimeError = this.getTranslation(1181)
      this.cdr.markForCheck()
      return
    }

    const { moveData } = this.pendingMove
    const durationMs = this.domain.toMs(moveData.newEnd) - this.domain.toMs(moveData.newStart)
    if (durationMs <= 0) {
      this.pendingMoveTimeError = this.getTranslation(1181)
      this.cdr.markForCheck()
      return
    }

    const nextStart = new Date(moveData.newStart)
    nextStart.setHours(hour, minute, 0, 0)
    const nextStartIso = this.domain.toLocalIso(nextStart.getTime())
    const nextEndIso = this.domain.toLocalIso(nextStart.getTime() + durationMs)

    this.pendingMove = {
      ...this.pendingMove,
      targetTime: time,
      moveData: {
        ...moveData,
        newStart: nextStartIso,
        newEnd: nextEndIso,
      },
    }
    this.pendingMoveTimeError = null
    this.cdr.markForCheck()
  }

  private getTranslation(code: number): string {
    return this.translateService.getTranslation(code)
  }

  /** Reverts a single optimistic move (called by the parent when the API fails). */
  revertMove(moveData: AgendaEventMove): void {
    const reversed: AgendaEventMove = {
      appointment: moveData.appointment,
      oldScheduleId: moveData.newScheduleId,
      newScheduleId: moveData.oldScheduleId,
      oldStart: moveData.newStart,
      oldEnd: moveData.newEnd,
      newStart: moveData.oldStart,
      newEnd: moveData.oldEnd,
    }
    this.calendarSlots = this.domain.applyMove(reversed, this.calendarContext)
    this.buildView()
    this.cdr.markForCheck()
  }

  // Reverses each move and applies them in reverse order so the resulting state matches
  // exactly what calendarSlots looked like before the batch was applied optimistically.
  revertMoveBatch(moves: AgendaEventMove[]): void {
    if (!moves.length) return
    for (let i = moves.length - 1; i >= 0; i--) {
      const m = moves[i]
      const reversed: AgendaEventMove = {
        appointment: m.appointment,
        oldScheduleId: m.newScheduleId,
        newScheduleId: m.oldScheduleId,
        oldStart: m.newStart,
        oldEnd: m.newEnd,
        newStart: m.oldStart,
        newEnd: m.oldEnd,
      }
      this.calendarSlots = this.domain.applyMove(reversed, this.calendarContext)
    }
    this.buildView()
    this.cdr.markForCheck()
  }

  protected trackByColumnKey(_: number, column: DenseColumn): string {
    return column.key
  }

  protected trackByDateGroup(_: number, group: DenseDateGroup): string {
    return group.date
  }

  protected trackByTime(_: number, row: DenseRow): string {
    return row.time
  }

  protected trackByCellSlot(_: number, cell: DenseCellSlot): string {
    const base = `${cell.slot.scheduleId}-${cell.slot.start}-${cell.slot.end}-${cell.type}`
    if (cell.type === 'appointment') return `${base}-${(cell.slot as AppointmentSlot).id}`
    return base
  }

  protected asAppointment(slot: CalendarSlot): AppointmentSlot {
    return slot as AppointmentSlot
  }

  protected asFree(slot: CalendarSlot): FreeSlot {
    return slot as FreeSlot
  }

  protected asBlocked(slot: CalendarSlot): BlockedSlot {
    return slot as BlockedSlot
  }

  protected formatColumnDate(date: string): string {
    return this.domain.formatDate(date)
  }

  protected formatColumnWeekday(date: string): string {
    return this.domain.formatWeekday(date, this.locale)
  }
}
